import { createContext, useContext, useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import AppScriptAPI from '../services/appscriptAPI';
import { canAccessLocation } from '../utils/accessControl';

const UserPermissionsContext = createContext();

export function UserPermissionsProvider({ children }) {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth0();
  const [userPermissions, setUserPermissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (!isAuthenticated || !user) {
      setUserPermissions(null);
      setLoading(false);
      return;
    }

    AppScriptAPI.setUserEmail(user.email);
    loadUserPermissions();
  }, [isAuthenticated, user, authLoading]);

  const loadUserPermissions = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔍 Checking user:', user.email);

      // Check if user exists
      const checkResult = await AppScriptAPI.checkUserExists(user.email);
      console.log('📋 Check result:', checkResult);

      if (!checkResult.exists) {
        // New user - add to Google Sheets
        console.log('✨ New user detected, adding to database...');

        const addResult = await AppScriptAPI.addNewUser({
          email: user.email,
          name: user.name || user.email,
          status: 'pending',
          access_level: '',
          access_type: '',
          allowed_provinces: '',
          allowed_districts: '',
          allowed_dsds: '',
          created_date: new Date().toISOString(),
          last_login: new Date().toISOString(),
        });

        console.log('✅ User added:', addResult);

        // Set pending permissions
        setUserPermissions({
          email: user.email,
          name: user.name,
          status: 'pending',
          access_level: null,
          access_type: null,
          message: 'Your account is pending approval by an administrator',
        });
      } else {
        // Existing user - update last login
        console.log('👤 Existing user, updating last login...');
        await AppScriptAPI.updateLastLogin(user.email);

        // Get full permissions
        const permissions = await AppScriptAPI.getUserPermissions(user.email);
        console.log('🔐 User permissions:', permissions);

        setUserPermissions({
          email: user.email,
          name: permissions.name || user.name,
          status: permissions.status,
          access_level: permissions.access_level,
          access_type: permissions.access_type,
          allowed_provinces: permissions.allowed_provinces,
          allowed_districts: permissions.allowed_districts,
          allowed_dsds: permissions.allowed_dsds,
        });
      }

      setLoading(false);
    } catch (err) {
      console.error('❌ Error loading permissions:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const refreshPermissions = async () => {
    if (isAuthenticated && user) {
      await loadUserPermissions();
    }
  };

  const value = {
    userPermissions,
    loading,
    error,
    refreshPermissions,
    isAdmin:     userPermissions?.access_type === 'admin',
    isEditor:    userPermissions?.access_type === 'editor' || userPermissions?.access_type === 'admin',
    isViewer:    userPermissions?.access_type === 'viewer',
    isPending:   userPermissions?.status === 'pending',
    isActive:    userPermissions?.status === 'active',
    isSuspended: userPermissions?.status === 'suspended',
    canAccessLocation: (loc) => canAccessLocation(userPermissions, loc),
  };

  return (
    <UserPermissionsContext.Provider value={value}>
      {children}
    </UserPermissionsContext.Provider>
  );
}

export function useUserPermissions() {
  const context = useContext(UserPermissionsContext);
  if (context === undefined) {
    throw new Error(
      'useUserPermissions must be used within UserPermissionsProvider'
    );
  }
  return context;
}
